import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/lib/auth";
import { CalendarPage } from "@/pages/CalendarPage";
import { ChatPage } from "@/pages/ChatPage";
import {
  ConnectionsAiTab,
  ConnectionsCalendarTab,
  ConnectionsChatTab,
  ConnectionsConnectedTab,
  ConnectionsLayout,
  ConnectionsMailTab,
  ConnectionsMeetingsTab,
} from "@/pages/ConnectionsPage";
import { HubAiPanel } from "@/components/HubAiPanel";
import { HubPage } from "@/pages/HubPage";
import { DocsSlackPage } from "@/pages/DocsSlackPage";
import { DocsTeamsPage } from "@/pages/DocsTeamsPage";
import { DocsZoomPage } from "@/pages/DocsZoomPage";
import { LoginPage } from "@/pages/LoginPage";
import { MailPage } from "@/pages/MailPage";
import { MeetingsPage } from "@/pages/MeetingsPage";
import { PrivacyPage } from "@/pages/PrivacyPage";
import { SecurityPage } from "@/pages/SecurityPage";
import { SlackLandingPage } from "@/pages/SlackLandingPage";
import { SupportPage } from "@/pages/SupportPage";
import { TasksPage } from "@/pages/TasksPage";
import { TermsPage } from "@/pages/TermsPage";
import { SettingsIndexRedirect, SettingsLayout } from "@/pages/SettingsLayout";
import {
  SettingsCalendarPage,
  SettingsChatPage,
  SettingsMailPage,
  SettingsMeetingsPage,
  SettingsNotificationsPage,
  SettingsProfilePage,
} from "@/pages/SettingsPage";

function Protected({ children }: { children: React.ReactNode }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="auth-screen muted">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <AppShell>{children}</AppShell>;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/slack" element={<SlackLandingPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/docs/zoom" element={<DocsZoomPage />} />
      <Route path="/docs/slack" element={<DocsSlackPage />} />
      <Route path="/docs/teams" element={<DocsTeamsPage />} />
      <Route
        path="/"
        element={
          <Protected>
            <HubPage />
          </Protected>
        }
      />
      <Route
        path="/mail"
        element={
          <Protected>
            <MailPage />
          </Protected>
        }
      />
      <Route
        path="/chat"
        element={
          <Protected>
            <ChatPage />
          </Protected>
        }
      />
      <Route
        path="/calendar"
        element={
          <Protected>
            <CalendarPage />
          </Protected>
        }
      />
      <Route
        path="/meetings"
        element={
          <Protected>
            <MeetingsPage />
          </Protected>
        }
      />
      <Route
        path="/tasks"
        element={
          <Protected>
            <TasksPage />
          </Protected>
        }
      />
      <Route
        path="/settings"
        element={
          <Protected>
            <SettingsLayout />
          </Protected>
        }
      >
        <Route index element={<SettingsIndexRedirect />} />
        <Route path="profile" element={<SettingsProfilePage />} />
        <Route path="mail" element={<SettingsMailPage />} />
        <Route path="mail/:section" element={<SettingsMailPage />} />
        <Route path="chat" element={<SettingsChatPage />} />
        <Route path="calendar" element={<SettingsCalendarPage />} />
        <Route path="meetings" element={<SettingsMeetingsPage />} />
        <Route path="notifications" element={<SettingsNotificationsPage />} />
        <Route path="connections" element={<ConnectionsLayout />}>
          <Route index element={<ConnectionsConnectedTab />} />
          <Route path="mail" element={<ConnectionsMailTab />} />
          <Route path="chat" element={<ConnectionsChatTab />} />
          <Route path="calendar" element={<ConnectionsCalendarTab />} />
          <Route path="meetings" element={<ConnectionsMeetingsTab />} />
          <Route path="ai" element={<ConnectionsAiTab />} />
        </Route>
      </Route>
      <Route
        path="/ai"
        element={
          <Protected>
            <div className="hub-ai-route">
              <HubAiPanel mode="page" />
            </div>
          </Protected>
        }
      />
      <Route path="/connectors" element={<Navigate to="/settings/connections" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
